/**
 * ContactsDirectory.jsx - every contact across every account, searchable and
 * filterable, with inline edit.
 *
 * A contact that belongs to a Site is listed once under that Site (the parent
 * Account also carries it, tagged, but listing both would duplicate the row).
 *
 * Phase 1.2: Title (the old free-text role), Role (C-03), LinkedIn URL and a
 * Primary flag (C-05), an account / site picker grouped by company to add a
 * contact and to move one between a company and its plants (C-02), a Role
 * filter and a CSV export. Every change refreshes the shared store
 * (fetchAll) - the list reads contacts off the accounts, so refreshing only
 * the fallback list left the directory showing stale rows until a reload.
 *
 * r43: Contact status (C-04) - anyone who left the company wears a grey tag,
 * sorts last and can be filtered on; they stay on file with their history.
 * Clicking a name opens the contact's own page (C-01) through onOpenContact.
 */
import { useState, useMemo, useEffect, useCallback } from 'react';
import { Icon } from '../ui/Icon';
import { Toast } from '../ui/Toast';
import ImportModal from '../ui/ImportModal';
import { useApp } from '../../context/AppContext';
import { CONTACT_ROLES, CONTACT_STATUSES, LEFT_COMPANY, isLeftCompany, pickField } from '../../data/crmStages';
import { exportCsv } from '../../utils/exportCsv';
import { ContactFields, LEFT_TAG } from './ContactDetailPanel';
import { contactPayload, contactFormFrom, contactExportRow, linkedinHref } from './crmFormHelpers';
import { BTN_PRIMARY, BTN_CANCEL, ERROR_LINE, tagStyle } from './crmStyles';

const blankForm = () => ({
  accountId: '',
  name: '',
  role: '',
  contactRole: '',
  email: '',
  phone: '',
  linkedinUrl: '',
  isPrimary: false,
  contactStatus: 'Active',
});
const NO_ROLE = '(no role)';
const statusOf = (c) => (isLeftCompany(c) ? LEFT_COMPANY : 'Active');
// the name + title cell; when it opens the contact it is a button that looks the same
const NAME_CELL = { minWidth: 160, flex: '0 0 auto', cursor: 'default' };
const NAME_BUTTON = {
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  textAlign: 'left',
  font: 'inherit',
  color: 'inherit',
  display: 'block',
};

// onOpenContact(contact) opens the contact's page; without it (tests, other hosts) a row is not clickable
function ContactsDirectory({ editable, deletable, onOpenContact }) {
  const { allAccounts, fetchAll } = useApp();
  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState('All');
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(blankForm);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState(blankForm);
  const [formErr, setFormErr] = useState('');
  const [toast, setToastState] = useState(null); // { message, tone }
  // tone 'error' shows a refusal in red; a plain message is a success
  const setToast = (message, tone) => setToastState(message ? { message, tone } : null);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [directContacts, setDirectContacts] = useState([]);
  const [showImport, setShowImport] = useState(false);

  const accountsById = useMemo(() => Object.fromEntries(allAccounts.map((a) => [a.id, a])), [allAccounts]);

  // Gather contacts from allAccounts (each account has a contacts array from GET /api/accounts)
  const accountContacts = useMemo(() => {
    const list = [];
    allAccounts.forEach((acc) => {
      (acc.contacts || []).forEach((c) => {
        if (c.siteName || c.site_name) return; // rolled up from a Site - it is listed under the Site itself (Paul #51)
        list.push({
          ...c,
          accountId: c.accountId || acc.id,
          accountName: c.accountName || acc.name,
          _key: c.id || `${acc.id}-${c.name}`,
        });
      });
    });
    return list;
  }, [allAccounts]);

  // Fallback: fetch from /api/accounts/contacts/all if account-embedded contacts are empty
  const fetchDirectContacts = useCallback(async () => {
    try {
      const res = await fetch('/api/accounts/contacts/all');
      if (res.ok) {
        const data = await res.json();
        setDirectContacts(
          data.map((c) => ({
            id: c.id,
            name: c.name,
            role: c.role,
            contactRole: c.contact_role,
            email: c.email,
            phone: c.phone,
            linkedinUrl: c.linkedin_url,
            isPrimary: c.is_primary,
            contactStatus: c.contact_status,
            accountId: c.account_id,
            accountName: c.account_name,
            _key: c.id,
          }))
        );
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
  }, []);

  useEffect(() => {
    fetchDirectContacts();
  }, [fetchDirectContacts]);

  // after any add / edit / move / delete: the store (what the list reads) and the fallback
  const refresh = useCallback(async () => {
    await Promise.all([fetchAll(), fetchDirectContacts()]);
  }, [fetchAll, fetchDirectContacts]);

  // Use account-embedded contacts first; if empty, fall back to direct fetch
  const allContacts = useMemo(() => {
    if (accountContacts.length > 0) return accountContacts;
    if (directContacts.length > 0) return directContacts;
    return [];
  }, [accountContacts, directContacts]);

  // a site's contacts read "Site · Company" so plants of one company sort and read together
  const placeLabel = useCallback(
    (c) => {
      const acct = accountsById[c.accountId];
      const parent = acct && pickField(acct, 'parentName', 'parent_name');
      return parent ? `${c.accountName} · ${parent}` : c.accountName;
    },
    [accountsById]
  );

  // Filter
  const filtered = useMemo(() => {
    let list = allContacts;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c) =>
        [c.name, c.role, pickField(c, 'contactRole', 'contact_role'), c.email, c.accountName].some((v) =>
          String(v || '')
            .toLowerCase()
            .includes(q)
        )
      );
    }
    if (accountFilter !== 'All') {
      list = list.filter((c) => c.accountName === accountFilter);
    }
    if (roleFilter !== 'All') {
      list = list.filter((c) => {
        const r = pickField(c, 'contactRole', 'contact_role') || '';
        return roleFilter === NO_ROLE ? !r : r === roleFilter;
      });
    }
    if (statusFilter !== 'All') {
      list = list.filter((c) => statusOf(c) === statusFilter);
    }
    // Sort
    if (sortBy === 'name') {
      list = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'account') {
      list = [...list].sort((a, b) => (a.accountName || '').localeCompare(b.accountName || ''));
    }
    // C-04: anyone who left the company goes last (the sort is stable, so each part keeps its order)
    return [...list].sort((a, b) => Number(isLeftCompany(a)) - Number(isLeftCompany(b)));
  }, [allContacts, search, accountFilter, roleFilter, statusFilter, sortBy]);

  const accountNames = useMemo(() => {
    const names = [...new Set(allContacts.map((c) => c.accountName).filter(Boolean))];
    names.sort();
    return names;
  }, [allContacts]);

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase();
  };

  const readError = async (res, fallback) => {
    const b = await res.json().catch(() => ({}));
    return b.error || fallback;
  };

  const handleAddContact = async () => {
    if (!addForm.accountId) {
      setFormErr('Pick the account or site this contact belongs to.');
      return;
    }
    if (!addForm.name.trim()) {
      setFormErr("Enter the contact's name.");
      return;
    }
    try {
      const res = await fetch('/api/accounts/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: addForm.accountId, ...contactPayload(addForm) }),
      });
      if (!res.ok) {
        setFormErr(await readError(res, 'Could not add the contact - try again.'));
        return;
      }
      setAddForm(blankForm());
      setShowAddForm(false);
      setFormErr('');
      setToast('Contact added');
      await refresh();
    } catch (err) {
      console.error('Add contact error:', err);
      setFormErr('Could not add the contact - try again.');
    }
  };

  // C-02: a different account / site in the edit form moves the contact there
  const handleSaveEdit = async (contact) => {
    if (!editForm.name.trim()) {
      setFormErr("Enter the contact's name.");
      return;
    }
    const moveTo = editForm.accountId && editForm.accountId !== contact.accountId ? editForm.accountId : null;
    try {
      const res = await fetch(`/api/accounts/${contact.accountId}/contacts/${contact.id || contact._key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contactPayload(editForm), ...(moveTo ? { account_id: moveTo } : {}) }),
      });
      if (!res.ok) {
        setFormErr(await readError(res, 'Could not save the contact - try again.'));
        return;
      }
      setEditingKey(null);
      setFormErr('');
      setToast(moveTo && accountsById[moveTo] ? `${editForm.name} moved to ${accountsById[moveTo].name}` : 'Contact updated');
      await refresh();
    } catch (err) {
      console.error('Edit contact error:', err);
      setFormErr('Could not save the contact - try again.');
    }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Delete contact "${contact.name}"?`)) return;
    try {
      const res = await fetch(`/api/accounts/${contact.accountId}/contacts/${contact.id || contact._key}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setToast('Contact deleted');
        await refresh();
      } else {
        setToast(await readError(res, 'Could not delete the contact.'), 'error');
      }
    } catch (err) {
      console.error('Delete contact error:', err);
      setToast('Could not delete the contact.', 'error');
    }
  };

  // C-03 / C-04 / C-05: name, title, role, email, phone, LinkedIn, primary, status, account / site - what the list shows
  const handleExport = () => {
    exportCsv(
      'contacts.csv',
      filtered.map((c) => contactExportRow(c, accountsById[c.accountId]))
    );
  };

  // the one contact form (C-02 / C-03 / C-04 / C-05); the account / site comes first here
  const fieldGrid = (form, setForm, pickerLabel, pickerPlaceholder) => (
    <ContactFields
      form={form}
      setForm={setForm}
      accounts={allAccounts}
      picker={{ label: pickerLabel, placeholder: pickerPlaceholder, first: true }}
      primaryLabel="Primary contact for this account / site"
      onEdited={() => setFormErr('')}
    />
  );

  const filterSelect = {
    padding: '7px 12px',
    borderRadius: 8,
    border: '1px solid #E8E8E8',
    fontSize: 11,
    fontFamily: 'Montserrat',
    outline: 'none',
    cursor: 'pointer',
    color: '#555',
    appearance: 'auto',
  };

  return (
    <div>
      {toast && <Toast message={toast.message} tone={toast.tone} onDone={() => setToast(null)} />}
      <ImportModal
        isOpen={showImport}
        onClose={() => {
          setShowImport(false);
          refresh();
        }}
        type="contacts"
        allAccounts={allAccounts}
        fetchAll={fetchAll}
      />

      {/* Toolbar */}
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #ECECEC',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          marginBottom: 16,
        }}
      >
        <div style={{ padding: '16px 22px' }}>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}
          >
            <div>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0D0D0D' }}>All Contacts</span>
              <span style={{ marginLeft: 8, fontSize: 10, color: '#aaa' }}>
                {allContacts.length} contacts across {allAccounts.filter((a) => !a.parentAccountId).length} companies
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleExport}
                disabled={filtered.length === 0}
                title="Download the contacts shown below as a CSV file"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 14px',
                  borderRadius: 6,
                  border: '1px solid #E8E8E8',
                  background: '#fff',
                  color: '#666',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: filtered.length ? 'pointer' : 'default',
                  opacity: filtered.length ? 1 : 0.5,
                  fontFamily: 'Montserrat',
                }}
              >
                <Icon name="download" size={11} color="#666" /> Export contacts
              </button>
              {editable && (
                <button
                  onClick={() => setShowImport(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 14px',
                    borderRadius: 6,
                    border: '1px solid #1B5E20',
                    background: '#fff',
                    color: '#1B5E20',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'Montserrat',
                  }}
                >
                  <Icon name="upload" size={11} color="#1B5E20" /> Import Contacts
                </button>
              )}
              {editable && (
                <button
                  onClick={() => {
                    setShowAddForm(!showAddForm);
                    setFormErr('');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'linear-gradient(135deg, #1B5E20, #2E7D32)',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#fff',
                    cursor: 'pointer',
                    fontFamily: 'Montserrat',
                    boxShadow: '0 2px 8px rgba(27,94,32,0.25)',
                  }}
                >
                  <Icon name="plus" size={12} color="#fff" /> Add Contact
                </button>
              )}
            </div>
          </div>

          {/* Add Contact Form */}
          {showAddForm && (
            <div style={{ background: '#FAFBFC', borderRadius: 10, padding: 18, marginBottom: 16, border: '1px solid #F0F0F0' }}>
              {fieldGrid(addForm, setAddForm, 'Account / site', 'Select account or site...')}
              {formErr && editingKey == null && <div style={{ ...ERROR_LINE, marginTop: 10 }}>{formErr}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={handleAddContact} style={BTN_PRIMARY}>
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setFormErr('');
                  }}
                  style={BTN_CANCEL}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Filter bar */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, maxWidth: 320, position: 'relative' }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, title, role, email or account..."
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 32px',
                  borderRadius: 8,
                  border: '1px solid #E8E8E8',
                  fontSize: 11,
                  fontFamily: 'Montserrat',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
                <Icon name="filter" size={12} color="#ccc" />
              </div>
            </div>
            <div>
              <select value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)} style={filterSelect}>
                <option value="All">All Accounts</option>
                {accountNames.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                style={filterSelect}
                title="Filter by contact role"
              >
                <option value="All">All Roles</option>
                {CONTACT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
                <option value={NO_ROLE}>No role set</option>
              </select>
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={filterSelect}
                title="Filter by contact status"
              >
                <option value="All">All Statuses</option>
                {CONTACT_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[
                ['name', 'Sort by Name'],
                ['account', 'Sort by Account'],
              ].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setSortBy(val)}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 20,
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'Montserrat',
                    border: `1px solid ${sortBy === val ? '#1B5E20' : '#E8E8E8'}`,
                    background: sortBy === val ? '#1B5E20' : '#fff',
                    color: sortBy === val ? '#fff' : '#666',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.length > 0 ? (
          filtered.map((c, i) => {
            const contactRole = pickField(c, 'contactRole', 'contact_role');
            const linkedin = pickField(c, 'linkedinUrl', 'linkedin_url');
            const primary = !!pickField(c, 'isPrimary', 'is_primary');
            const left = isLeftCompany(c);
            const title = c.role && c.role !== '-' ? c.role : '';
            const canOpen = !!onOpenContact && c.id != null;
            const NameCell = canOpen ? 'button' : 'div';
            return (
              <div
                key={c._key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 18px',
                  borderRadius: 10,
                  border: '1px solid #ECECEC',
                  background: left ? '#FAFAFA' : '#fff',
                  transition: 'box-shadow 0.15s',
                  boxShadow: hoveredIdx === i ? '0 4px 16px rgba(0,0,0,0.07)' : '0 1px 4px rgba(0,0,0,0.02)',
                  flexWrap: 'wrap',
                }}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
              >
                {editingKey === c._key ? (
                  <div style={{ flex: 1 }}>
                    {fieldGrid(editForm, setEditForm, 'Account / site (Move to...)', 'Move to...')}
                    {formErr && <div style={{ ...ERROR_LINE, marginTop: 10 }}>{formErr}</div>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button onClick={() => handleSaveEdit(c)} style={BTN_PRIMARY}>
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingKey(null);
                          setFormErr('');
                        }}
                        style={BTN_CANCEL}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Avatar */}
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: '#0D0D0D',
                        color: '#43A047',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 800,
                        fontFamily: 'Montserrat',
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(c.name)}
                    </div>
                    {/* Name + Title / Role - opens the contact's page (C-01); a real button, so the keyboard reaches it */}
                    <NameCell
                      {...(canOpen ? { type: 'button', onClick: () => onOpenContact(c), title: `Open ${c.name}` } : {})}
                      style={canOpen ? { ...NAME_CELL, ...NAME_BUTTON } : NAME_CELL}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: left ? '#777' : '#0D0D0D',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span style={canOpen && hoveredIdx === i ? { textDecoration: 'underline' } : undefined}>{c.name}</span>
                        {primary && (
                          <span style={tagStyle('#1B5E20', '#E8F5E9')} title={`Primary contact for ${c.accountName}`}>
                            Primary
                          </span>
                        )}
                        {left && (
                          <span style={LEFT_TAG} title="Left the company - kept for history, hidden from task and attendee pickers">
                            {LEFT_COMPANY}
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: 10, color: '#aaa', marginTop: 1, display: 'flex', gap: 5 }}>
                        <span>{title || '-'}</span>
                        {contactRole && (
                          <>
                            <span>·</span>
                            <span style={{ color: '#6A4C93', fontWeight: 700 }}>{contactRole}</span>
                          </>
                        )}
                      </span>
                    </NameCell>
                    {/* Email */}
                    <div
                      style={{
                        flex: 1,
                        fontSize: 11,
                        color: '#1B5E20',
                        fontWeight: 600,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {c.email && c.email !== '-' ? c.email : '-'}
                    </div>
                    {/* Phone */}
                    <div style={{ fontSize: 11, color: '#666', minWidth: 110 }}>{c.phone && c.phone !== '-' ? c.phone : '-'}</div>
                    {/* LinkedIn */}
                    <div style={{ minWidth: 64 }}>
                      {linkedin ? (
                        <a
                          href={linkedinHref(linkedin)}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 10, fontWeight: 700, color: '#0A66C2' }}
                        >
                          LinkedIn ↗
                        </a>
                      ) : (
                        <span style={{ fontSize: 10, color: '#ddd' }}>-</span>
                      )}
                    </div>
                    {/* Account / site pill */}
                    <div
                      style={{
                        background: '#F0F2F0',
                        borderRadius: 20,
                        padding: '4px 10px',
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#555',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {placeLabel(c)}
                    </div>
                    {/* Edit / move */}
                    {editable && (
                      <button
                        title="Edit or move this contact"
                        onClick={() => {
                          setEditingKey(c._key);
                          setEditForm({ ...contactFormFrom(c), accountId: c.accountId || '' });
                          setFormErr('');
                        }}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
                      >
                        <Icon name="edit" size={13} color="#888" />
                      </button>
                    )}
                    {/* Delete */}
                    {deletable && (
                      <button
                        title="Delete this contact"
                        onClick={() => handleDelete(c)}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
                      >
                        <Icon name="trash" size={13} color="#B71C1C" />
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: 60,
              color: '#ccc',
              fontSize: 11,
              background: '#fff',
              borderRadius: 12,
              border: '1px solid #ECECEC',
            }}
          >
            {search || accountFilter !== 'All' || roleFilter !== 'All' || statusFilter !== 'All'
              ? 'No contacts match your filters'
              : 'No contacts found across any accounts'}
          </div>
        )}
      </div>
    </div>
  );
}
export default ContactsDirectory;
